/**
 * v1.3.17 — Deferred AI Jobs Queue (Phase 2A of Manual Airplane Mode).
 *
 * Separate IndexedDB store dedicated to AI-heavy actions that a user
 * triggered while manual Airplane Mode was ON. Each entry describes
 * a `job_type` + parameters — NOT a payload — and is replayed by
 * invoking the same axios call the online button would have made,
 * against the same endpoint. So a deferred find-manuals is
 * indistinguishable server-side from a live find-manuals.
 *
 * DESIGN INVARIANT
 * ----------------
 * This module is COMPLETELY INDEPENDENT from every other offline
 * queue. Different DB name, different store, different drain path.
 * Deleting this file breaks ONLY the "queue AI action" feature —
 * all other offline flows are untouched.
 *
 * Storage
 *   Database: fmiq_deferred_ai_jobs_db
 *   Store:    deferred_ai_jobs
 *
 * Record shape:
 *   {
 *     id: <autoincrement>,
 *     job_id: <client-generated UUID for cross-tab dedup>,
 *     job_type: 'find_manuals' | 'reanalyze_asset' | 'site_review'
 *              | 'assess_damage' | 'eul_enrich' | 'detect_siblings',
 *     params: { ... }         // job-type-specific args (small!)
 *     label: <string>          // human-readable "Find manuals for
 *                              // Carrier 25HCE460A00" for the drawer
 *     queued_at: <ISO string>,
 *     status: 'pending' | 'running' | 'completed' | 'failed',
 *     retry_count: <int>,
 *     last_error: <string|null>,
 *   }
 *
 * Sync trigger: subscribeConnectivity in `deferredAiJobsSync.js`.
 */

const DB_NAME = 'fmiq_deferred_ai_jobs_db';
const DB_VERSION = 1;
const STORE = 'deferred_ai_jobs';

let dbInstance = null;
let dbFailed = false;

const _openDB = () => new Promise((resolve, reject) => {
  if (dbFailed) return reject(new Error('deferred-ai-jobs DB failed earlier'));
  if (dbInstance) return resolve(dbInstance);
  if (!window.indexedDB) {
    dbFailed = true;
    return reject(new Error('IndexedDB not available'));
  }
  const req = window.indexedDB.open(DB_NAME, DB_VERSION);
  req.onupgradeneeded = (ev) => {
    const db = ev.target.result;
    if (!db.objectStoreNames.contains(STORE)) {
      const s = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      s.createIndex('job_id', 'job_id', { unique: false });
      s.createIndex('status', 'status', { unique: false });
      s.createIndex('job_type', 'job_type', { unique: false });
    }
  };
  req.onsuccess = () => { dbInstance = req.result; resolve(dbInstance); };
  req.onerror = () => { dbFailed = true; reject(req.error); };
});

const _uuid = () => (
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : 'jid-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Add a job to the queue. Returns the created record (with autoincrement id).
 *
 * @param {Object} spec
 * @param {string} spec.job_type
 * @param {Object} [spec.params]
 * @param {string} [spec.label]     Human-readable summary for the drawer.
 */
export const enqueueDeferredJob = async ({ job_type, params = {}, label = '' }) => {
  if (!job_type) throw new Error('enqueueDeferredJob: job_type is required');
  const db = await _openDB();
  const record = {
    job_id: _uuid(),
    job_type,
    params,
    label: label || job_type,
    queued_at: new Date().toISOString(),
    status: 'pending',
    retry_count: 0,
    last_error: null,
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE], 'readwrite');
    const req = tx.objectStore(STORE).add(record);
    req.onsuccess = () => resolve({ ...record, id: req.result });
    req.onerror = () => reject(req.error);
  });
};

/** Return all deferred jobs sorted by queued_at desc. */
export const listDeferredJobs = async () => {
  try {
    const db = await _openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction([STORE], 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => {
        const rows = req.result || [];
        rows.sort((a, b) => (b.queued_at || '').localeCompare(a.queued_at || ''));
        resolve(rows);
      };
      req.onerror = () => reject(req.error);
    });
  } catch (_) {
    return [];
  }
};

/** Return only pending / running jobs. */
export const listPendingDeferredJobs = async () => {
  const all = await listDeferredJobs();
  return all.filter(j => j.status === 'pending' || j.status === 'running');
};

/** Count pending. */
export const getPendingDeferredCount = async () => {
  const rows = await listPendingDeferredJobs();
  return rows.length;
};

/** Remove one job by autoincrement id. */
export const removeDeferredJob = async (id) => {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE], 'readwrite');
    const req = tx.objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
};

/** Update status + optionally the last_error. */
export const updateDeferredJobStatus = async (id, status, last_error = null) => {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE], 'readwrite');
    const store = tx.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const row = getReq.result;
      if (!row) return resolve();
      row.status = status;
      if (last_error != null) row.last_error = last_error;
      if (status === 'running') row.retry_count = (row.retry_count || 0) + 1;
      const putReq = store.put(row);
      putReq.onsuccess = () => resolve(row);
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
};

/** Clear all completed jobs from the store. */
export const clearCompletedDeferredJobs = async () => {
  const rows = await listDeferredJobs();
  const completed = rows.filter(r => r.status === 'completed');
  for (const r of completed) {
    // eslint-disable-next-line no-await-in-loop
    await removeDeferredJob(r.id);
  }
  return completed.length;
};

// ---------------------------------------------------------------------------
// Pub-sub for the queue drawer (Phase 2B will subscribe to this).
// ---------------------------------------------------------------------------
const _listeners = new Set();
export const subscribeDeferredJobs = (cb) => {
  _listeners.add(cb);
  return () => { _listeners.delete(cb); };
};
export const publishDeferredJobsChanged = () => {
  _listeners.forEach(cb => { try { cb(); } catch (_) { /* isolated */ } });
};
